'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowUpRight,
  Briefcase,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ExternalLink,
  Globe,
  GraduationCap,
  Heart,
  Layers,
  Lock,
  Loader2,
  MapPin,
  Sparkles,
  Target,
  TrendingUp,
  X,
} from 'lucide-react'
import type {
  CareerMapResult,
  CareerMatch,
  CareerJobSearchResult,
  AdzunaJob,
} from '@/app/actions'

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

export function extractJobKeywords(job: { title: string; company?: string | null }): string[] {
  const stopWords = new Set([
    'and', 'the', 'for', 'from', 'with', 'that', 'this', 'will', 'have', 'been',
    'your', 'must', 'able', 'work', 'team', 'join', 'role', 'jobs', 'job', 'position',
    'looking', 'seeking', 'hiring', 'needed', 'wanted', 'level', 'based', 'full', 'time',
    'part', 'remote', 'hybrid', 'onsite',
  ])
  const keywords: string[] = []
  if (job.title) {
    const titleWords = job.title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').split(/[\s\-/]+/)
      .filter(w => w.length >= 4 && !stopWords.has(w))
    keywords.push(...titleWords)
  }
  if (job.company) {
    const companyWords = job.company.toLowerCase().replace(/[^a-z0-9\s]/g, '').split(/[\s,&.]+/)
      .filter(w => w.length >= 3 && !stopWords.has(w))
    keywords.push(...companyWords)
  }
  return [...new Set(keywords)].slice(0, 10)
}

export function formatSalary(value: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
}

function formatCompact(value: number) {
  if (value >= 1000) return `$${Math.round(value / 1000)}k`
  return `$${value}`
}

// ---------------------------------------------------------------------------
// Tier classification
// ---------------------------------------------------------------------------

export type MatchTier = 'direct' | 'strong' | 'growth'

export function classifyMatch(m: CareerMatch): MatchTier {
  const gapCount = m.skill_gaps?.length ?? 0
  if (m.confidence === 'high' || (m.composite_score >= 0.75 && gapCount === 0)) return 'direct'
  if (m.composite_score >= 0.60 && gapCount <= 1) return 'strong'
  return 'growth'
}

const TIER_META: Record<MatchTier, {
  label: string
  subtitle: string
  icon: typeof CheckCircle2
  accent: string
  border: string
  bg: string
  badge: string
  text: string
}> = {
  direct: {
    label: 'Direct Match',
    subtitle: 'Your military experience translates directly.',
    icon: CheckCircle2,
    accent: 'text-emerald-600 dark:text-emerald-400',
    border: 'border-emerald-200 dark:border-emerald-800',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    text: 'text-emerald-700 dark:text-emerald-300'
  },
  strong: {
    label: 'Strong Match',
    subtitle: 'Competitive with minor upskilling.',
    icon: TrendingUp,
    accent: 'text-blue-600 dark:text-blue-400',
    border: 'border-blue-200 dark:border-blue-800',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    text: 'text-blue-700 dark:text-blue-300'
  },
  growth: {
    label: 'Growth Opportunity',
    subtitle: 'Stretch roles — may require training.',
    icon: ArrowUpRight,
    accent: 'text-sky-600 dark:text-sky-400',
    border: 'border-sky-200 dark:border-sky-800',
    bg: 'bg-sky-50 dark:bg-sky-900/20',
    badge: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
    text: 'text-sky-700 dark:text-sky-300'
  }
}

const TIER_ORDER: MatchTier[] = ['direct', 'strong', 'growth']

const confidenceColor: Record<string, string> = {
  high: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  medium: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
  low: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
}

const severityColor: Record<string, string> = {
  high: 'text-rose-600 dark:text-rose-400',
  moderate: 'text-teal-600 dark:text-teal-400',
  low: 'text-slate-500 dark:text-slate-400'
}

// ---------------------------------------------------------------------------
// Job swimlane tier config
// ---------------------------------------------------------------------------

const SWIMLANE_META: Record<string, { label: string; icon: typeof Lock; accent: string; bg: string; badge: string }> = {
  military: {
    label: 'Defense & Cleared',
    icon: Lock,
    accent: 'text-slate-700 dark:text-slate-200',
    bg: 'bg-slate-50 dark:bg-slate-800/30',
    badge: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
  },
  direct: {
    label: 'Direct Match',
    icon: CheckCircle2,
    accent: 'text-emerald-700 dark:text-emerald-300',
    bg: 'bg-emerald-50/50 dark:bg-emerald-900/10',
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
  },
  strong: {
    label: 'Strong Match',
    icon: TrendingUp,
    accent: 'text-blue-700 dark:text-blue-300',
    bg: 'bg-blue-50/50 dark:bg-blue-900/10',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
  },
  growth: {
    label: 'Growth Opportunity',
    icon: ArrowUpRight,
    accent: 'text-sky-700 dark:text-sky-300',
    bg: 'bg-sky-50/50 dark:bg-sky-900/10',
    badge: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300'
  },
  spotlight: {
    label: 'Top Jobs Nationwide',
    icon: Sparkles,
    accent: 'text-teal-700 dark:text-teal-300',
    bg: 'bg-teal-50/50 dark:bg-teal-900/10',
    badge: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
  },
  skills: {
    label: 'Your Skills',
    icon: Target,
    accent: 'text-rose-700 dark:text-rose-300',
    bg: 'bg-rose-50/50 dark:bg-rose-900/10',
    badge: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
  }
}

// ---------------------------------------------------------------------------
// Compact job card — salary-first design
// ---------------------------------------------------------------------------

function JobCardCompact({
  job,
  tier,
  isLiked = false,
  onToggleLike,
  onViewDetails,
}: {
  job: AdzunaJob
  tier: string
  isLiked?: boolean
  onToggleLike?: (job: AdzunaJob) => void
  onViewDetails: (job: AdzunaJob, tier: string) => void
}) {
  const [liked, setLiked] = useState(isLiked)
  const [likeLoading, setLikeLoading] = useState(false)
  const hasSalary = typeof job.salary_min === 'number' || typeof job.salary_max === 'number'

  useEffect(() => { setLiked(isLiked) }, [isLiked])

  const handleLike = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!onToggleLike || likeLoading) return
    setLikeLoading(true)
    setLiked(!liked)
    try { onToggleLike(job) } catch { setLiked(liked) }
    setLikeLoading(false)
  }

  const desc = (job.description ?? '').toLowerCase()
  const title = (job.title ?? '').toLowerCase()
  const hasClearance = desc.includes('clearance') || desc.includes('secret') || title.includes('clearance') || tier === 'military'
  const isNational = tier === 'spotlight'

  const salaryText = hasSalary
    ? job.salary_min && job.salary_max
      ? job.salary_min === job.salary_max
        ? formatCompact(job.salary_min)
        : `${formatCompact(job.salary_min)} – ${formatCompact(job.salary_max)}`
      : job.salary_min
        ? `From ${formatCompact(job.salary_min)}`
        : `Up to ${formatCompact(job.salary_max!)}`
    : null

  return (
    <div
      className="group relative flex w-[260px] shrink-0 cursor-pointer flex-col rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-4 shadow-sm transition-all hover:shadow-md snap-start"
      onClick={() => onViewDetails(job, tier)}
    >
      {salaryText ? (
        <p className="text-xl font-extrabold tracking-tight text-emerald-600 dark:text-emerald-400">
          {salaryText}
        </p>
      ) : (
        <p className="text-sm font-semibold text-slate-400 dark:text-[var(--text-3)]">Salary not listed</p>
      )}

      <h4 className="mt-2 line-clamp-2 text-sm font-bold leading-snug text-slate-900 dark:text-[var(--text-1)]">
        {job.title}
      </h4>

      {job.company && (
        <p className="mt-1 truncate text-xs font-medium text-slate-500 dark:text-[var(--text-2)]">
          {job.company}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-1.5">
        {job.location && (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
            <MapPin className="h-2.5 w-2.5" />
            {job.location.length > 20 ? job.location.slice(0, 18) + '…' : job.location}
          </span>
        )}
        {hasClearance && (
          <span className="inline-flex items-center gap-1 rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-semibold text-teal-700 dark:bg-teal-900/40 dark:text-teal-300">
            <Lock className="h-2.5 w-2.5" />
            Clearance
          </span>
        )}
        {isNational && (
          <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-semibold text-teal-600 dark:bg-teal-900/30 dark:text-teal-300">
            <Globe className="h-2.5 w-2.5" />
            National
          </span>
        )}
      </div>

      <div className="flex-1" />

      <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3 dark:border-[var(--border)]">
        <span className="text-[11px] font-semibold text-blue-600 group-hover:text-blue-700 dark:text-blue-400 dark:group-hover:text-blue-300">
          See Details →
        </span>
        {onToggleLike ? (
          <button
            onClick={handleLike}
            disabled={likeLoading}
            className="flex h-7 w-7 items-center justify-center rounded-lg transition hover:bg-slate-100 dark:hover:bg-slate-700"
            aria-label={liked ? 'Unlike job' : 'Like job'}
          >
            <Heart
              className={`h-3.5 w-3.5 transition ${
                liked ? 'fill-rose-500 text-rose-500' : 'text-slate-300 hover:text-rose-400'
              } ${likeLoading ? 'opacity-50' : ''}`}
            />
          </button>
        ) : (
          <div className="flex h-7 w-7 items-center justify-center" title="Sign in to like jobs">
            <Heart className="h-3.5 w-3.5 text-slate-200 dark:text-slate-600" />
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Horizontal swimlane with scroll controls
// ---------------------------------------------------------------------------

function Swimlane({
  tier,
  label,
  sublabel,
  jobs,
  likedJobUrls,
  onToggleLike,
  onViewDetails,
}: {
  tier: string
  label: string
  sublabel: string
  jobs: AdzunaJob[]
  likedJobUrls: Set<string>
  onToggleLike?: (job: AdzunaJob) => void
  onViewDetails: (job: AdzunaJob, tier: string) => void
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const meta = SWIMLANE_META[tier] ?? SWIMLANE_META.military
  const Icon = meta.icon

  const checkScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 4)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4)
  }, [])

  useEffect(() => {
    checkScroll()
    const el = scrollRef.current
    if (!el) return
    el.addEventListener('scroll', checkScroll, { passive: true })
    const ro = new ResizeObserver(checkScroll)
    ro.observe(el)
    return () => { el.removeEventListener('scroll', checkScroll); ro.disconnect() }
  }, [checkScroll, jobs])

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current
    if (!el) return
    const amount = el.clientWidth * 0.75
    el.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' })
  }

  if (jobs.length === 0) return null

  return (
    <section className="relative">
      <div className="mb-3 flex items-center gap-2.5 px-1">
        <div className={`flex h-7 w-7 items-center justify-center rounded-lg ${meta.badge}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className={`text-sm font-bold ${meta.accent}`}>{label}</h3>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500 dark:bg-slate-700 dark:text-slate-400">
              {jobs.length}
            </span>
          </div>
          <p className="text-[11px] text-slate-400 dark:text-[var(--text-3)]">{sublabel}</p>
        </div>
      </div>

      <div className="relative">
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute -left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--surface-1)] shadow-md transition hover:bg-[var(--surface-2)]"
          >
            <ChevronLeft className="h-4 w-4 text-slate-600 dark:text-slate-300" />
          </button>
        )}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute -right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--surface-1)] shadow-md transition hover:bg-[var(--surface-2)]"
          >
            <ChevronRight className="h-4 w-4 text-slate-600 dark:text-slate-300" />
          </button>
        )}

        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto scroll-smooth pb-2 snap-x snap-mandatory scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {jobs.map((job, idx) => (
            <JobCardCompact
              key={`${tier}-${idx}`}
              job={job}
              tier={tier}
              isLiked={likedJobUrls.has(job.redirect_url)}
              onToggleLike={onToggleLike}
              onViewDetails={onViewDetails}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Job detail drawer/modal
// ---------------------------------------------------------------------------

function JobDetailDrawer({
  job,
  tier,
  isLiked,
  onToggleLike,
  onClose,
}: {
  job: AdzunaJob
  tier: string
  isLiked: boolean
  onToggleLike?: (job: AdzunaJob) => void
  onClose: () => void
}) {
  const hasSalary = typeof job.salary_min === 'number' || typeof job.salary_max === 'number'
  const meta = SWIMLANE_META[tier] ?? SWIMLANE_META.military
  const desc = (job.description ?? '').toLowerCase()
  const title = (job.title ?? '').toLowerCase()
  const hasClearance = desc.includes('clearance') || desc.includes('secret') || title.includes('clearance') || tier === 'military'
  const cleanDesc = (job.description ?? '').replace(/<[^>]*>/g, '')

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:m-4 dark:bg-[var(--surface-1)] max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 transition hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600"
        >
          <X className="h-4 w-4 text-slate-500 dark:text-slate-300" />
        </button>

        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${meta.badge}`}>
          <meta.icon className="h-3 w-3" />
          {meta.label}
        </span>

        {hasSalary && (
          <p className="mt-4 text-3xl font-extrabold text-emerald-600 dark:text-emerald-400">
            {job.salary_min && job.salary_max
              ? job.salary_min === job.salary_max
                ? formatSalary(job.salary_min)
                : `${formatSalary(job.salary_min)} – ${formatSalary(job.salary_max)}`
              : job.salary_min
                ? `From ${formatSalary(job.salary_min)}`
                : `Up to ${formatSalary(job.salary_max!)}`}
            <span className="ml-2 text-sm font-medium text-slate-400">/year</span>
          </p>
        )}

        <h3 className="mt-3 text-lg font-bold text-slate-900 dark:text-[var(--text-1)]">{job.title}</h3>
        {job.company && (
          <p className="mt-1 text-sm font-medium text-slate-600 dark:text-[var(--text-2)]">{job.company}</p>
        )}

        <div className="mt-3 flex flex-wrap gap-1.5">
          {job.location && (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
              <MapPin className="h-3 w-3" /> {job.location}
            </span>
          )}
          {hasClearance && (
            <span className="inline-flex items-center gap-1 rounded-full bg-teal-100 px-2.5 py-1 text-xs font-semibold text-teal-700 dark:bg-teal-900/40 dark:text-teal-300">
              <Lock className="h-3 w-3" /> Clearance Required
            </span>
          )}
          {tier === 'spotlight' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-600 dark:bg-teal-900/30 dark:text-teal-300">
              <Globe className="h-3 w-3" /> National
            </span>
          )}
        </div>

        {cleanDesc && (
          <div className="mt-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-[var(--text-3)]">Description</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-[var(--text-2)]">{cleanDesc}</p>
          </div>
        )}

        <div className="mt-6 flex items-center gap-3">
          <a
            href={job.redirect_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[var(--accent)] px-4 py-3 text-sm font-semibold text-[var(--accent-contrast)] shadow-lg shadow-[var(--accent)]/25 transition hover:bg-[var(--accent-hover)]"
          >
            View Full Listing <ExternalLink className="h-4 w-4" />
          </a>
          {onToggleLike && (
            <button
              onClick={() => onToggleLike(job)}
              className={`flex h-12 w-12 items-center justify-center rounded-xl border transition ${
                isLiked
                  ? 'border-rose-200 bg-rose-50 dark:border-rose-800 dark:bg-rose-900/20'
                  : 'border-[var(--border)] bg-[var(--surface-1)] hover:bg-[var(--surface-2)]'
              }`}
              aria-label={isLiked ? 'Unlike job' : 'Like job'}
            >
              <Heart className={`h-5 w-5 ${isLiked ? 'fill-rose-500 text-rose-500' : 'text-slate-400'}`} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Score bar
// ---------------------------------------------------------------------------

function ScoreBar({ value, label }: { value: number; label: string }) {
  const pct = Math.round(value * 100)
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-[var(--text-3)]">
        <span>{label}</span>
        <span className="font-semibold text-slate-700 dark:text-[var(--text-1)]">{pct}%</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
        <div
          className="h-full rounded-full bg-blue-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Career match card
// ---------------------------------------------------------------------------

function MatchCard({ match }: { match: CareerMatch }) {
  const [expanded, setExpanded] = useState(false)
  const hasGaps = match.skill_gaps && match.skill_gaps.length > 0

  return (
    <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-5 shadow-sm transition hover:shadow-md">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
              {match.rank}
            </span>
            <h3 className="truncate text-sm font-bold text-slate-900 dark:text-[var(--text-1)]">
              {match.soc_title}
            </h3>
          </div>
          <p className="mt-1 text-xs text-slate-400 dark:text-[var(--text-3)]">SOC {match.soc_code}</p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${confidenceColor[match.confidence] ?? confidenceColor.low}`}>
            {match.confidence}
          </span>
          {match.local_salary ? (
            <>
              <span className="text-lg font-extrabold text-emerald-600 dark:text-emerald-400">
                {formatSalary(match.local_salary.a_median)}
              </span>
              <span className="text-[10px] uppercase tracking-wide text-emerald-500/70">
                local median{match.local_salary.data_source === 'col_estimate' ? '*' : ''}
              </span>
            </>
          ) : (
            <>
              <span className="text-lg font-extrabold text-slate-900 dark:text-[var(--text-1)]">
                {formatSalary(match.median_salary_national)}
              </span>
              <span className="text-[10px] uppercase tracking-wide text-slate-400">national</span>
            </>
          )}
        </div>
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-500 dark:text-[var(--text-3)]">Match Score</span>
          <span className="font-bold text-blue-600 dark:text-blue-400">{Math.round(match.composite_score * 100)}%</span>
        </div>
        <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
          <div
            className="h-full rounded-full bg-gradient-to-r from-blue-500 to-sky-500 transition-all"
            style={{ width: `${Math.round(match.composite_score * 100)}%` }}
          />
        </div>
      </div>

      <p className="mt-3 text-xs leading-relaxed text-slate-600 dark:text-[var(--text-2)]">{match.explanation}</p>

      {hasGaps && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {match.skill_gaps.map((gap) => (
            <span
              key={gap.skill_id}
              className={`inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-[11px] font-medium dark:border-[var(--border)] ${severityColor[gap.severity] ?? severityColor.low}`}
            >
              <Target className="h-3 w-3" />{gap.skill_name}
            </span>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="mt-3 flex w-full items-center justify-center gap-1 rounded-xl py-2 text-xs font-semibold text-blue-600 transition hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
      >
        {expanded ? 'Hide Details' : 'View Details'}
        {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>

      {expanded && (
        <div className="mt-3 space-y-4 border-t border-slate-100 pt-4 dark:border-[var(--border)]">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-[var(--text-3)]">Score Breakdown</p>
            <div className="space-y-2">
              <ScoreBar value={match.score_breakdown.occupational_fit} label="Occupational Fit" />
              <ScoreBar value={match.score_breakdown.rank_leadership} label="Rank & Leadership" />
              <ScoreBar value={match.score_breakdown.technical_adjacency} label="Technical Adjacency" />
              <ScoreBar value={match.score_breakdown.transferable_skills} label="Transferable Skills" />
            </div>
          </div>
          <div className="space-y-2 text-xs text-slate-500 dark:text-[var(--text-3)]">
            <p><span className="font-semibold text-slate-700 dark:text-[var(--text-2)]">Occupational Fit:</span> {match.score_breakdown.occupational_fit_reason}</p>
            <p><span className="font-semibold text-slate-700 dark:text-[var(--text-2)]">Leadership:</span> {match.score_breakdown.rank_leadership_reason}</p>
            <p><span className="font-semibold text-slate-700 dark:text-[var(--text-2)]">Technical:</span> {match.score_breakdown.technical_adjacency_reason}</p>
            <p><span className="font-semibold text-slate-700 dark:text-[var(--text-2)]">Skills:</span> {match.score_breakdown.transferable_skills_reason}</p>
          </div>
          {hasGaps && (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-[var(--text-3)]">Recommended Training</p>
              <div className="space-y-3">
                {match.skill_gaps.map((gap) => (
                  <div key={gap.skill_id} className="rounded-xl bg-slate-50 p-3 dark:bg-[var(--surface-2)]">
                    <p className={`text-xs font-semibold ${severityColor[gap.severity] ?? ''}`}>
                      {gap.skill_name} <span className="text-slate-400">({gap.severity} gap)</span>
                    </p>
                    <ul className="mt-1.5 space-y-1">
                      {gap.recommended_training.map((t, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-slate-600 dark:text-[var(--text-2)]">
                          <GraduationCap className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
                          <span>
                            <span className="font-medium">{t.name}</span>
                            <span className="text-slate-400"> — {t.provider}</span>
                            {t.duration_hours > 0 && <span className="text-slate-400"> · {t.duration_hours}h</span>}
                            {t.estimated_cost_usd === 0
                              ? <span className="ml-1 font-semibold text-emerald-600 dark:text-emerald-400">Free</span>
                              : <span className="text-slate-400"> · {formatSalary(t.estimated_cost_usd)}</span>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Unified results view (exported)
// ---------------------------------------------------------------------------

export default function UnifiedResults({
  result,
  jobResults,
  jobsLoading,
  likedJobUrls,
  onToggleLike,
}: {
  result: CareerMapResult
  jobResults: CareerJobSearchResult[]
  jobsLoading: boolean
  likedJobUrls: Set<string>
  onToggleLike?: (job: AdzunaJob) => void
}) {
  const [detailJob, setDetailJob] = useState<{ job: AdzunaJob; tier: string } | null>(null)
  const [showAllCareers, setShowAllCareers] = useState(false)

  const tiers = useMemo(() => {
    const grouped: Record<MatchTier, CareerMatch[]> = { direct: [], strong: [], growth: [] }
    for (const m of result.matches) grouped[classifyMatch(m)].push(m)
    return grouped
  }, [result.matches])

  const hasLocalSalary = result.matches.some((m) => m.local_salary)
  const geo = result.geoContext

  const salaryRange = useMemo(() => {
    const salaries = result.matches.map((m) => m.local_salary?.a_median ?? m.median_salary_national).filter(Boolean)
    if (salaries.length === 0) return null
    return { min: Math.min(...salaries), max: Math.max(...salaries) }
  }, [result.matches])

  const localResults = jobResults.filter((r) => r.tier !== 'spotlight' && r.tier !== 'skills')
  const spotlightResults = jobResults.filter((r) => r.tier === 'spotlight')
  const skillsResults = jobResults.filter((r) => r.tier === 'skills')

  const onViewDetails = useCallback((job: AdzunaJob, tier: string) => {
    setDetailJob({ job, tier })
  }, [])

  const topMatches = result.matches.slice(0, 3)

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      {/* Results Summary */}
      <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 shadow-sm dark:border-[var(--border)] dark:from-[var(--surface-2)] dark:to-[var(--surface-1)] md:p-8">
        {geo && geo.city && (
          <div className="mb-5 flex items-center justify-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-1.5 dark:bg-blue-900/20">
              <MapPin className="h-3.5 w-3.5 text-blue-500" />
              <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                {geo.city}, {geo.state}
              </span>
              {geo.col_ratio && geo.col_ratio !== 1 && (
                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-600 dark:bg-blue-900/40 dark:text-blue-400">
                  {geo.col_ratio > 1 ? '+' : ''}{Math.round((geo.col_ratio - 1) * 100)}% vs national
                </span>
              )}
            </div>
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-3">
          <div className="flex flex-col items-center justify-center text-center md:items-start md:text-left">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Your MOS</p>
            <p className="mt-1 text-2xl font-extrabold text-slate-900 dark:text-[var(--text-1)]">{result.mosCode}</p>
            <p className="text-sm text-slate-500 dark:text-[var(--text-2)]">{result.mosTitle}</p>
            <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">{result.zoneName}</p>
          </div>

          <div className="flex flex-col items-center justify-center text-center">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
              {hasLocalSalary ? 'Local Salary Range' : 'Salary Range'}
            </p>
            {salaryRange ? (
              <p className="mt-1 text-2xl font-extrabold text-emerald-600 dark:text-emerald-400">
                {formatCompact(salaryRange.min)} – {formatCompact(salaryRange.max)}
              </p>
            ) : (
              <p className="mt-1 text-lg text-slate-400">—</p>
            )}
            <p className="text-xs text-slate-400">across {result.totalMatches} career matches</p>
          </div>

          <div className="flex flex-col items-center justify-center text-center md:items-end md:text-right">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Career Matches</p>
            <div className="mt-1 flex items-center gap-1.5">
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                {tiers.direct.length} direct
              </span>
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-bold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                {tiers.strong.length} strong
              </span>
              <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-bold text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
                {tiers.growth.length} growth
              </span>
            </div>
            {result.responsibilityDescription && (
              <p className="mt-2 text-[11px] text-slate-400">
                {result.tier} tier · {result.rank}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Top Career Paths */}
      <div>
        <div className="mb-4 flex items-center justify-between px-1">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-blue-500" />
            <h2 className="text-base font-bold text-slate-900 dark:text-[var(--text-1)]">Top Career Paths</h2>
          </div>
          <button
            type="button"
            onClick={() => setShowAllCareers((prev) => !prev)}
            className="text-xs font-semibold text-blue-600 transition hover:text-blue-700 dark:text-blue-400"
          >
            {showAllCareers ? 'Show less' : `View all ${result.totalMatches} careers →`}
          </button>
        </div>

        {!showAllCareers ? (
          <div className="flex gap-3 overflow-x-auto pb-2 snap-x" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {topMatches.map((match) => {
              const tier = classifyMatch(match)
              const meta = TIER_META[tier]
              return (
                <div
                  key={`${match.soc_code}-${match.rank}`}
                  className="w-[280px] shrink-0 rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-4 shadow-sm snap-start"
                >
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${meta.badge}`}>{meta.label}</span>
                    <span className={`text-xs font-semibold ${confidenceColor[match.confidence] ?? ''} rounded-full px-2 py-0.5`}>
                      {match.confidence}
                    </span>
                  </div>
                  <h4 className="mt-2 text-sm font-bold text-slate-900 dark:text-[var(--text-1)]">{match.soc_title}</h4>
                  <p className="mt-2 text-xl font-extrabold text-emerald-600 dark:text-emerald-400">
                    {formatSalary(match.local_salary?.a_median ?? match.median_salary_national)}
                  </p>
                  <p className="text-[10px] text-slate-400">
                    {match.local_salary ? 'local median' : 'national median'}
                  </p>
                  <div className="mt-2">
                    <p className="mb-1 text-[10px] text-slate-400 dark:text-[var(--text-3)]">Career Match Score</p>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                        <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-sky-500" style={{ width: `${Math.round(match.composite_score * 100)}%` }} />
                      </div>
                      <span className="text-xs font-bold text-blue-600 dark:text-blue-400">{Math.round(match.composite_score * 100)}%</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="space-y-6">
            {TIER_ORDER.map((tierKey) => {
              const matches = tiers[tierKey]
              if (matches.length === 0) return null
              const meta = TIER_META[tierKey]
              const Icon = meta.icon

              return (
                <section key={tierKey}>
                  <div className={`mb-4 rounded-2xl border ${meta.border} ${meta.bg} p-4`}>
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${meta.badge}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className={`text-base font-bold ${meta.accent}`}>{meta.label}</h2>
                          <span className={`rounded-full ${meta.badge} px-2 py-0.5 text-[11px] font-semibold`}>{matches.length} {matches.length === 1 ? 'role' : 'roles'}</span>
                        </div>
                        <p className="mt-0.5 text-xs text-slate-500 dark:text-[var(--text-2)]">{meta.subtitle}</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {matches.map((match) => (
                      <MatchCard key={`${match.soc_code}-${match.rank}`} match={match} />
                    ))}
                  </div>
                </section>
              )
            })}
          </div>
        )}
      </div>

      {/* Job Swimlanes */}
      <div>
        <div className="mb-5 flex items-center gap-2 px-1">
          <Briefcase className="h-4 w-4 text-blue-500" />
          <h2 className="text-base font-bold text-slate-900 dark:text-[var(--text-1)]">Live Job Listings</h2>
          {jobsLoading && <Loader2 className="h-4 w-4 animate-spin text-blue-500" />}
          {!jobsLoading && jobResults.some((r) => r.jobs.length > 0) && (
            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-600 dark:bg-blue-900/40 dark:text-blue-400">
              {jobResults.reduce((sum, r) => sum + r.jobs.length, 0)} jobs
            </span>
          )}
        </div>

        {jobsLoading && (
          <div className="flex items-center justify-center gap-2 rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-8 shadow-sm">
            <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
            <span className="text-sm text-[var(--text-2)]">Searching live listings...</span>
          </div>
        )}

        {!jobsLoading && (
          <div className="space-y-8">
            {localResults.map((group) => (
              <Swimlane
                key={group.searchTerm}
                tier={group.tier}
                label={SWIMLANE_META[group.tier]?.label ?? group.tier}
                sublabel={group.label}
                jobs={group.jobs}
                likedJobUrls={likedJobUrls}
                onToggleLike={onToggleLike}
                onViewDetails={onViewDetails}
              />
            ))}
            {spotlightResults.map((group) => (
              <Swimlane
                key="spotlight"
                tier="spotlight"
                label="Top Jobs Nationwide"
                sublabel={group.label}
                jobs={group.jobs}
                likedJobUrls={likedJobUrls}
                onToggleLike={onToggleLike}
                onViewDetails={onViewDetails}
              />
            ))}
            {skillsResults.map((group) => (
              <Swimlane
                key="skills"
                tier="skills"
                label={group.label}
                sublabel={group.searchTerm}
                jobs={group.jobs}
                likedJobUrls={likedJobUrls}
                onToggleLike={onToggleLike}
                onViewDetails={onViewDetails}
              />
            ))}
          </div>
        )}
      </div>

      {/* Job detail drawer */}
      {detailJob && (
        <JobDetailDrawer
          job={detailJob.job}
          tier={detailJob.tier}
          isLiked={likedJobUrls.has(detailJob.job.redirect_url)}
          onToggleLike={onToggleLike}
          onClose={() => setDetailJob(null)}
        />
      )}
    </div>
  )
}
